/**
 * This component allows authenticated user with permissions to access the protected Routes.
 */
import Cookies from 'js-cookie';
import { lazy, Suspense, useMemo } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { clearCookiesAndLocalstorage } from '../CommonUtilities/CommonUtilities';
import type { ComponentName } from '../Components';

interface PageProps {
  page: string;
  path: string;
}

const loadComponent = async (componentName: ComponentName) => {
  // This uses tree-shaking and doesn't expose individual file paths
  const components = await import('../Components');
  const Component = components[componentName];

  if (!Component) {
    throw new Error(`Component not found: ${componentName}`);
  }

  return { default: Component };
};

function AuthorisedRoute(props: PageProps) {
  const { page, path } = props;

  const Component = useMemo(() => {
    try {
      return lazy(() => loadComponent(page as ComponentName));
    } catch (error) {
      console.error(`Failed to load component: ${page}`, error);
      return null;
    }
  }, [page]);

  if (!Component) {
    return <Redirect to="/Login" />;
  }

  if (
    path !== '/Login' &&
    (!Cookies.get('access') ||
      !Cookies.get('refresh') ||
      !Cookies.get('isValidated'))
  ) {
    clearCookiesAndLocalstorage(false);
    return <Redirect to="/Login" />;
  }

  return (
    <Suspense fallback={<></>}>
      <Route path={path} component={Component} />
    </Suspense>
  );
}

export default AuthorisedRoute;
